import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { JobListResponse, JobListRow, NoteStreamItem, NoteStreamResponse } from '@unitedos/shared';
import { LinkedText } from '../components/LinkedText.js';
import { ApiError, fetchJobs, fetchLatestNotes } from '../lib/api.js';
import { appendPage, type MyJobsPages, myJobsQuery, NO_PAGES, noteAuthor, noteExcerpt, noteHref } from '../lib/home.js';
import { chipClass, jobStatusTone, statusLabel } from '../lib/jobsFormat.js';
import { useRoles, useSessionContext } from '../lib/session.js';
import { formatDateTime, formatRelative, formatShortDate } from '../lib/time.js';
import type { Load } from '../lib/useLoad.js';
import { useLoad } from '../lib/useLoad.js';

/**
 * Home in Jobber's structure (design 17, D-088, D-089): the date and greeting, the Workflow row
 * (Requests, Quotes, Jobs, Invoices in Jobber's order — the Jobs card over the jobs list's own
 * status counts, the other three placeholders until their screens exist), My jobs — the active jobs the signed-in
 * person sells or manages, paged as the page scrolls — and the latest communications across every
 * job in a column that scrolls by itself. Every read is one the API already serves; nothing here is
 * a second copy of Jobber's data.
 */
export function HomePage() {
  const { session } = useSessionContext();
  const roles = useRoles();
  const email = session?.user.email ?? '';
  const first = email.split('@')[0]?.split(/[._-]/)[0] ?? '';
  const who = first ? first.charAt(0).toUpperCase() + first.slice(1) : 'there';
  // The clock ticks once a minute, so every relative time stays true while the tab sits open.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);
  const hour = now.getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  const jobsLoad = useLoad((signal) => fetchJobs(new URLSearchParams({ size: '1' }), signal));
  const notesLoad = useLoad((signal) => fetchLatestNotes(10, signal));


  return (
    <section className="page">
      <div className="home-head">
        <div>
          <p className="eyebrow">{now.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}</p>
          <h1>
            {greeting}, {who}
          </h1>
        </div>
      </div>

      <h2>Workflow</h2>
      <div className="workflow">
        <SoonCard kind="Requests" edge="request" />
        <QuotesCard />
        <JobsCard load={jobsLoad.load} />
        <SoonCard kind="Invoices" edge="invoice" />
      </div>

      <div className="home-cols">
        <div>
          <MyJobs isAdmin={roles.includes('admin')} />
        </div>

        <div className="home-comms">
          <div className="home-section__head">
            <h2>Latest communications</h2>
          </div>
          <Communications load={notesLoad.load} now={now} />
        </div>
      </div>
    </section>
  );
}

// ------------------------------------------------------------------ Workflow --

/** The Jobs card: Jobber's own status counts, each a link into the list filtered to that tab. */
function JobsCard({ load }: { readonly load: Load<JobListResponse> }) {
  const count = (key: string): number | null => (load.state === 'ok' ? (load.data.statusCounts.find((c) => c.key === key)?.count ?? null) : null);
  const figure = (key: string): string => {
    const n = count(key);
    return n === null ? (load.state === 'error' ? '—' : '…') : n.toLocaleString('en-US');
  };
  return (
    <div className="card card--edge-job workflow__card">
      <p className="workflow__kind">Jobs</p>
      <Link className="workflow__big" to="/jobs?status=requires_invoicing" title="Jobber's own status">
        <span className="workflow__num">{figure('requires_invoicing')}</span>
        <span className="workflow__label">Requires invoicing</span>
      </Link>
      <div className="workflow__lines">
        <Link className="workflow__line" to="/jobs?status=active">
          <span>Active</span>
          <b>{figure('active')}</b>
        </Link>
        <Link className="workflow__line" to="/jobs?status=late">
          <span>Late</span>
          <b>{figure('late')}</b>
        </Link>
      </div>
      {load.state === 'error' && <p className="text-bad text-small">{load.message}</p>}
    </div>
  );
}

/** Quotes (design 27): the card opens the list; its figures wait for the worksheet (slice 2). */
function QuotesCard() {
  return (
    <Link className="card card--edge-quote workflow__card" to="/quotes">
      <p className="workflow__kind">Quotes</p>
    </Link>
  );
}

/** A kind whose list screen is not built (Dakota, 2026-10-05): the card holds its place in Jobber's row and says so. */
function SoonCard({ kind, edge }: { readonly kind: string; readonly edge: 'request' | 'quote' | 'invoice' }) {
  return (
    <div className={`card card--edge-${edge} workflow__card workflow__card--soon`}>
      <p className="workflow__kind">{kind}</p>
      <p className="workflow__soon">Coming soon</p>
    </div>
  );
}

// ------------------------------------------------------------------- My jobs --

type MyJobsState =
  | { readonly kind: 'pages'; readonly pages: MyJobsPages; readonly loading: boolean; readonly error: string | null }
  | { readonly kind: 'unlinked' };

/**
 * The active jobs the signed-in person sells or manages (D-089), a page at a time as the list
 * scrolls into view. A sign-in with no Jobber link gets one sentence instead of an empty list.
 */
function useMyJobs(): { readonly state: MyJobsState; readonly more: () => void } {
  const [state, setState] = useState<MyJobsState>({ kind: 'pages', pages: NO_PAGES, loading: false, error: null });
  // The next page to ask for and the request in flight live in refs, so the guard is never a state
  // updater's side effect: React runs an updater twice in development, and the fetch that lived in
  // one was started twice and aborted by the effect's cleanup, leaving "Loading…" for good (2026-10-05).
  const nextPage = useRef<number | null>(NO_PAGES.next);
  const inFlight = useRef<AbortController | null>(null);

  const more = useCallback(() => {
    const page = nextPage.current;
    if (page === null || inFlight.current !== null) return;
    const abort = new AbortController();
    inFlight.current = abort;
    setState((s) => (s.kind === 'pages' ? { ...s, loading: true, error: null } : s));
    fetchJobs(myJobsQuery(page), abort.signal)
      .then((r) => {
        if (abort.signal.aborted) return;
        inFlight.current = null;
        setState((cur) => {
          if (cur.kind !== 'pages') return cur;
          const pages = appendPage(cur.pages, page, r.rows, r.total);
          nextPage.current = pages.next;
          return { kind: 'pages', pages, loading: false, error: null };
        });
      })
      .catch((err: unknown) => {
        if (abort.signal.aborted) return;
        inFlight.current = null;
        if (err instanceof ApiError && err.code === 'not_linked') {
          nextPage.current = null;
          setState({ kind: 'unlinked' });
        } else {
          setState((cur) => (cur.kind === 'pages' ? { ...cur, loading: false, error: err instanceof ApiError ? err.message : 'Unexpected error calling the API.' } : cur));
        }
      });
  }, []);

  useEffect(() => {
    more();
    return () => {
      inFlight.current?.abort();
      inFlight.current = null;
    };
  }, [more]);

  return { state, more };
}

function MyJobs({ isAdmin }: { readonly isAdmin: boolean }) {
  const { state, more } = useMyJobs();
  const sentinel = useRef<HTMLDivElement | null>(null);
  const wantMore = state.kind === 'pages' && !state.loading && state.pages.next !== null;

  // The next page comes when the end of the list scrolls into view.
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !wantMore) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) more();
    });
    io.observe(el);
    return () => io.disconnect();
  }, [wantMore, more]);

  const total = state.kind === 'pages' ? state.pages.total : null;
  return (
    <div>
      <div className="home-section__head">
        <h2>
          My jobs {total !== null && <span className="tab__count">{total.toLocaleString('en-US')}</span>}
        </h2>
      </div>
      {state.kind === 'unlinked' ? (
        <div className="empty">
          {isAdmin ? (
            <>
              Your sign-in is not linked to a Jobber user yet — link it in <Link to="/settings/accounts">Settings → Accounts</Link>.
            </>
          ) : (
            'Your sign-in is not linked to a Jobber user yet — an admin links it in Settings → Accounts.'
          )}
        </div>
      ) : state.pages.total === 0 ? (
        <div className="empty">No active jobs where you are the salesperson or account manager.</div>
      ) : (
        <>
          {state.pages.rows.length > 0 && (
            <table className="table table--cards">
              <thead>
                <tr>
                  <th>Job</th>
                  <th>Client</th>
                  <th>Status</th>
                  <th>Start</th>
                </tr>
              </thead>
              <tbody>
                {state.pages.rows.map((row) => (
                  <MyJobRow key={row.jobNumber} row={row} />
                ))}
              </tbody>
            </table>
          )}
          {state.error && <p className="text-bad text-small">{state.error}</p>}
          <div ref={sentinel} className="myjobs__more">
            {state.loading ? <span className="text-muted text-small">Loading…</span> : state.error ? <button type="button" className="button button--small" onClick={more}>Retry</button> : null}
          </div>
        </>
      )}
    </div>
  );
}

function MyJobRow({ row }: { readonly row: JobListRow }) {
  return (
    <tr>
      <td>
        <Link to={`/jobs/${row.jobNumber}`} className="myjobs__job">
          <b>#{row.jobNumber}</b> <span title={row.title ?? undefined}>{row.title ?? '—'}</span>
        </Link>
      </td>
      <td data-label="Client">
        <span className="myjobs__client" title={row.propertyAddress ?? undefined}>
          {row.clientName ?? '—'}
        </span>
      </td>
      <td data-label="Status">{row.status ? <span className={chipClass(jobStatusTone(row.status))}>{statusLabel(row.status)}</span> : <span className="text-muted">—</span>}</td>
      <td className="text-nowrap" data-label="Start">
        {row.startAt ? formatShortDate(new Date(row.startAt)) : <span className="text-muted">—</span>}
      </td>
    </tr>
  );
}

// ---------------------------------------------------- Latest communications --

function Communications({ load, now }: { readonly load: Load<NoteStreamResponse>; readonly now: Date }) {
  if (load.state === 'loading') return <p className="text-muted">Loading…</p>;
  if (load.state === 'error') return <p className="text-bad">{load.message}</p>;
  if (load.data.items.length === 0) return <div className="empty">No notes yet.</div>;
  return (
    <div className="comms">
      {load.data.items.map((note) => (
        <NoteCard key={`${note.origin}-${note.jobNumber}-${note.createdAt}-${note.message?.slice(0, 20)}`} note={note} now={now} />
      ))}
    </div>
  );
}

const ORIGIN_LABEL: Readonly<Record<string, string>> = { JobNote: 'Job note', QuoteNote: 'Quote note', RequestNote: 'Request note', ClientNote: 'Client note' };

function NoteCard({ note, now }: { readonly note: NoteStreamItem; readonly now: Date }) {
  const href = noteHref(note);
  const created = note.createdAt ? new Date(note.createdAt) : null;
  const when = created ? (now.getTime() - created.getTime() < 48 * 3_600_000 ? formatRelative(created, now) : formatDateTime(created)) : '';
  const body = (
    <>
      <span className="comm__top">
        <span className="comm__who">{noteAuthor(note)}</span>
        <span className="comm__when" title={created ? formatDateTime(created) : undefined}>
          {when}
        </span>
      </span>
      <span className="comm__job" title={note.jobTitle ?? undefined}>
        {note.jobNumber !== null && <b>#{note.jobNumber}</b>} {note.jobTitle}
      </span>
      {/* A card that opens its job is itself a link, and an anchor may not sit in one: its addresses are links on the job's Notes (design 45 §15.4). */}
      <span className="comm__body">{href ? noteExcerpt(note.message) : <LinkedText text={noteExcerpt(note.message)} />}</span>
      <span className="comm__foot">
        <span className="origin">{ORIGIN_LABEL[note.origin] ?? note.origin}</span>
        {note.fileCount > 0 && <span>{note.fileCount === 1 ? '1 file' : `${note.fileCount} files`}</span>}
      </span>
    </>
  );
  return href ? (
    <Link className="comm" to={href}>
      {body}
    </Link>
  ) : (
    <div className="comm">{body}</div>
  );
}
